/**
 * Variant Gallery - Filters product images based on selected variant
 *
 * This script listens for variant changes from Dawn's existing variant selector
 * and updates the image gallery to show only images associated with the selected variant.
 *
 * FILTERING METHOD: Alt Text Matching
 * Images are filtered by matching their alt text against the selected variant's option values.
 *
 * NAMING CONVENTION:
 * - Set image alt text to start with the variant option value (e.g., "Red", "Blue", "Large")
 * - Examples: "Red - Front View", "Blue", "Classic - Detail"
 * - Images without matching alt text are treated as "shared" and shown for all variants
 *
 * Features:
 * - Filters main gallery images by alt text matching variant options
 * - Filters thumbnail navigation by variant
 * - Falls back to showing all images if no images match the variant
 * - Preserves Dawn's existing gallery functionality (zoom, swipe, etc.)
 * - Updates slider counter after filtering
 *
 * @requires pubsub.js (for subscribe/publish functions)
 * @requires global.js (for PUB_SUB_EVENTS constant)
 */

if (!customElements.get('variant-gallery-filter')) {
  customElements.define(
    'variant-gallery-filter',
    class VariantGalleryFilter extends HTMLElement {
      constructor() {
        super();
        this.sectionId = null;
        this.productId = null;
        this.mediaData = [];
        this.variantData = [];
        this.currentVariantId = null;
        this.unsubscriber = null;
      }

      connectedCallback() {
        this.initializeFromData();
        this.subscribeToVariantChanges();
        // Apply initial filtering based on selected variant
        this.applyInitialFilter();
      }

      disconnectedCallback() {
        if (this.unsubscriber) {
          this.unsubscriber();
        }
      }

      /**
       * Initialize gallery data from embedded JSON
       */
      initializeFromData() {
        // Find the data script for this section
        const productInfo = this.closest('product-info');
        if (!productInfo) return;

        this.sectionId = productInfo.dataset.section;
        const dataScript = document.getElementById(`VariantGalleryData-${this.sectionId}`);

        if (dataScript) {
          try {
            const data = JSON.parse(dataScript.textContent);
            this.productId = data.productId;
            this.mediaData = data.media || [];
            this.variantData = data.variants || [];
            this.currentVariantId = data.selectedVariantId;
            this.variantOptions = data.variantOptions || [];
          } catch (e) {
            console.error('Error parsing variant gallery data:', e);
          }
        }
      }

      /**
       * Apply initial filtering based on the initially selected variant
       */
      applyInitialFilter() {
        if (this.currentVariantId && this.variantData.length > 0) {
          const currentVariant = this.variantData.find(v => v.id === this.currentVariantId);
          if (currentVariant && currentVariant.options) {
            this.filterGalleryByOptions(currentVariant.options);
          }
        }
      }

      /**
       * Subscribe to Dawn's variant change events
       */
      subscribeToVariantChanges() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
          console.warn('Variant Gallery: pubsub.js not loaded');
          return;
        }

        // Subscribe to variant changes
        this.unsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange.bind(this));
      }

      /**
       * Handle variant change event from Dawn's variant selector
       * @param {Object} payload - Event payload containing variant data
       */
      handleVariantChange({ data }) {
        if (!data || !data.variant) return;

        // Check if this change is for our section
        if (data.sectionId !== this.sectionId) return;

        const newVariantId = data.variant.id;

        // Skip if same variant
        if (newVariantId === this.currentVariantId) return;

        this.currentVariantId = newVariantId;

        // Get variant options (e.g., ["Red", "Large"])
        const variantOptions = this.getVariantOptions(newVariantId);
        this.filterGalleryByOptions(variantOptions);
      }

      /**
       * Get option values for a specific variant
       * @param {number} variantId - Variant ID
       * @returns {string[]} Array of option values (e.g., ["Red", "Large"])
       */
      getVariantOptions(variantId) {
        const variant = this.variantData.find(v => v.id === variantId);
        return variant ? variant.options : [];
      }

      /**
       * Filter gallery images based on variant option values matching alt text
       * @param {string[]} optionValues - Array of variant option values to match
       */
      filterGalleryByOptions(optionValues) {
        const mediaGallery = document.querySelector(`#MediaGallery-${this.sectionId}`);
        if (!mediaGallery) return;

        // Get all media items (main gallery)
        const mainGalleryItems = mediaGallery.querySelectorAll('.variant-gallery-media-item');
        // Get all thumbnail items
        const thumbnailItems = mediaGallery.querySelectorAll('.variant-gallery-thumbnail');

        let visibleCount = 0;
        let firstVisibleItem = null;
        let hasMatchingImages = false;

        // First pass: check if any images match the variant options
        mainGalleryItems.forEach((item) => {
          const altText = item.dataset.mediaAlt || '';
          if (this.altTextMatchesOptions(altText, optionValues)) {
            hasMatchingImages = true;
          }
        });

        // Second pass: apply visibility
        mainGalleryItems.forEach((item) => {
          const altText = item.dataset.mediaAlt || '';
          let shouldShow = false;

          if (!hasMatchingImages) {
            // Fallback: show all images if no images match variant options
            shouldShow = true;
          } else if (this.altTextMatchesOptions(altText, optionValues)) {
            // Image alt text matches one of the variant options
            shouldShow = true;
          } else if (!this.altTextMatchesAnyVariant(altText)) {
            // Image doesn't match any variant option - it's a shared/common image
            shouldShow = true;
          }

          // Apply visibility
          if (shouldShow) {
            item.classList.remove('hidden');
            visibleCount++;
            if (!firstVisibleItem) {
              firstVisibleItem = item;
            }
          } else {
            item.classList.add('hidden');
            item.classList.remove('is-active');
          }
        });

        // Filter thumbnail items with same logic
        thumbnailItems.forEach((item) => {
          const altText = item.dataset.mediaAlt || '';
          let shouldShow = false;

          if (!hasMatchingImages) {
            shouldShow = true;
          } else if (this.altTextMatchesOptions(altText, optionValues)) {
            shouldShow = true;
          } else if (!this.altTextMatchesAnyVariant(altText)) {
            shouldShow = true;
          }

          if (shouldShow) {
            item.classList.remove('hidden');
          } else {
            item.classList.add('hidden');
            const button = item.querySelector('button');
            if (button) {
              button.removeAttribute('aria-current');
            }
          }
        });

        // Set the first visible item as active
        if (firstVisibleItem) {
          // Remove active class from all items
          mainGalleryItems.forEach((item) => item.classList.remove('is-active'));
          // Set first visible as active
          firstVisibleItem.classList.add('is-active');

          // Update thumbnail active state
          const targetMediaId = firstVisibleItem.dataset.mediaId;
          thumbnailItems.forEach((thumb) => {
            const button = thumb.querySelector('button');
            if (button) {
              if (thumb.dataset.target === targetMediaId) {
                button.setAttribute('aria-current', 'true');
              } else {
                button.removeAttribute('aria-current');
              }
            }
          });

          // Scroll gallery to show first visible item
          this.scrollToFirstVisible(firstVisibleItem);
        }

        // Update slider counter
        this.updateSliderCounter(visibleCount);

        // Reset slider component
        this.resetSliderComponent();
      }

      /**
       * Check if alt text matches any of the given option values
       * @param {string} altText - Image alt text
       * @param {string[]} optionValues - Array of option values to check
       * @returns {boolean} True if alt text starts with or contains any option value
       */
      altTextMatchesOptions(altText, optionValues) {
        if (!altText || !optionValues || optionValues.length === 0) return false;

        const normalizedAlt = altText.toLowerCase().trim();

        return optionValues.some(option => {
          if (!option) return false;
          const normalizedOption = option.toLowerCase().trim();
          // Check if alt text starts with the option value
          // This allows for "Red - Front View" to match "Red"
          return normalizedAlt.startsWith(normalizedOption) ||
                 normalizedAlt.includes(normalizedOption + ' ') ||
                 normalizedAlt.includes(normalizedOption + '-') ||
                 normalizedAlt.includes(normalizedOption + '_') ||
                 normalizedAlt === normalizedOption;
        });
      }

      /**
       * Check if alt text matches any variant option across all variants
       * Used to identify "shared" images that don't belong to any specific variant
       * @param {string} altText - Image alt text
       * @returns {boolean} True if alt text matches any variant option
       */
      altTextMatchesAnyVariant(altText) {
        if (!altText) return false;

        // Get all unique option values from all variants
        const allOptions = new Set();
        this.variantData.forEach(variant => {
          if (variant.options) {
            variant.options.forEach(opt => {
              if (opt) allOptions.add(opt.toLowerCase().trim());
            });
          }
        });

        const normalizedAlt = altText.toLowerCase().trim();

        return Array.from(allOptions).some(option => {
          return normalizedAlt.startsWith(option) ||
                 normalizedAlt.includes(option + ' ') ||
                 normalizedAlt.includes(option + '-') ||
                 normalizedAlt.includes(option + '_') ||
                 normalizedAlt === option;
        });
      }

      /**
       * Scroll gallery to show the first visible item
       * @param {HTMLElement} item - The first visible gallery item
       */
      scrollToFirstVisible(item) {
        const slider = document.querySelector(`#Slider-Gallery-${this.sectionId}`);
        if (slider && item) {
          slider.scrollTo({
            left: 0,
            behavior: 'smooth',
          });
        }
      }

      /**
       * Update the slider counter display
       * @param {number} count - Number of visible items
       */
      updateSliderCounter(count) {
        const totalCounter = document.querySelector(`#GalleryViewer-${this.sectionId} .slider-counter--total`);
        const currentCounter = document.querySelector(`#GalleryViewer-${this.sectionId} .slider-counter--current`);

        if (totalCounter) {
          totalCounter.textContent = count;
        }
        if (currentCounter) {
          currentCounter.textContent = '1';
        }
      }

      /**
       * Reset the slider component to recalculate pages
       */
      resetSliderComponent() {
        const sliderComponent = document.querySelector(`#GalleryViewer-${this.sectionId}`);
        if (sliderComponent && typeof sliderComponent.resetPages === 'function') {
          sliderComponent.resetPages();
        }

        // Also reset thumbnail slider
        const thumbnailSlider = document.querySelector(`#GalleryThumbnails-${this.sectionId}`);
        if (thumbnailSlider && typeof thumbnailSlider.resetPages === 'function') {
          thumbnailSlider.resetPages();
        }
      }
    }
  );
}

/**
 * Initialize variant gallery filtering when DOM is ready
 * This function creates filter instances for galleries that use the variant gallery template
 */
function initializeVariantGalleryFilters() {
  // Find all product-info elements with variant gallery data
  document.querySelectorAll('product-info[data-section]').forEach((productInfo) => {
    const sectionId = productInfo.dataset.section;
    const dataScript = document.getElementById(`VariantGalleryData-${sectionId}`);

    // Only initialize if variant gallery data exists
    if (dataScript) {
      // Check if filter is already initialized
      const existingFilter = productInfo.querySelector('variant-gallery-filter');
      if (existingFilter) return;

      // Create and insert the variant gallery filter component
      const filter = document.createElement('variant-gallery-filter');
      filter.style.display = 'none'; // Hidden element, just for functionality
      productInfo.appendChild(filter);
    }
  });
}

// Initialize on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeVariantGalleryFilters);
} else {
  initializeVariantGalleryFilters();
}

// Also reinitialize on section load (for theme editor support)
document.addEventListener('shopify:section:load', (event) => {
  setTimeout(initializeVariantGalleryFilters, 100);
});
